import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Row, Col, Badge, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { FaArrowLeft, FaTrash } from 'react-icons/fa';

const LEARNER_ROLE_VALUE = 1;

const canRemoveAssignment = (assignment) => {
  if (!assignment?.available_from) {
    return false;
  }
  return new Date() < new Date(assignment.available_from);
};

const AssessmentAssignments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assessmentTitle, setAssessmentTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    assignment_type: 'user',
    target_role: 'learner',
    user_id: '',
    group_id: '',
    available_from: '',
    available_to: ''
  });

  const formatScheduleDate = (value) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserDisplayName = (user) => {
    if (!user) return '-';
    return user.username || user.email || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-';
  };

  const getAssignmentRoleName = (assignment) =>
    assignment.roleName || assignment.user?.roleMaster?.name || 'Learner';

  const assignedUserIds = useMemo(
    () => new Set(assignments.map((item) => Number(item.user_id)).filter(Boolean)),
    [assignments]
  );

  const availableUsers = useMemo(
    () => users.filter((user) => !assignedUserIds.has(Number(user.id))),
    [users, assignedUserIds]
  );

  const platformGroups = useMemo(
    () => groups.filter((group) => group.organization_id === null || group.organization_id === undefined),
    [groups]
  );

  const loadUsersFromMaster = useCallback(async () => {
    try {
      setUsersLoading(true);
      const usersRes = await api.users.getAll();
      const baseUsers = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data || []);

      // Super Admin: only non-organization learners
      const filtered = baseUsers.filter((user) => {
        const roleValue = parseInt(user.role, 10);
        const hasNoOrg = user.organization_id === null || user.organization_id === undefined || user.organization_id === '';
        return (
          hasNoOrg
          && roleValue === LEARNER_ROLE_VALUE
          && Number(user.deleted || 0) !== 1
          && Number(user.status || 1) === 1
        );
      });

      setUsers(filtered);
    } catch (error) {
      toast.error('Failed to load users from User Master');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchInitial = useCallback(async () => {
    try {
      setLoading(true);
      const [assignmentsRes, groupsRes, assessmentRes] = await Promise.all([
        api.assessments.getAssignments(Number(id)),
        api.groups.list(),
        api.assessments.getById(id)
      ]);

      const assessmentData = assessmentRes.data?.data || assessmentRes.data;
      setAssessmentTitle(assessmentData?.title || '');
      setGroups(Array.isArray(groupsRes.data?.data) ? groupsRes.data.data : []);
      setAssignments(assignmentsRes.data?.data || []);
    } catch (error) {
      toast.error('Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  useEffect(() => {
    if (formData.assignment_type === 'user') {
      loadUsersFromMaster();
    }
  }, [formData.assignment_type, loadUsersFromMaster]);

  const handleAssignmentTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      assignment_type: value,
      target_role: 'learner',
      user_id: '',
      group_id: ''
    }));
  };

  const handleAssign = async () => {
    if (!formData.available_from || !formData.available_to) {
      toast.error('Schedule Start and Schedule End are required');
      return;
    }

    if (formData.assignment_type === 'user' && !formData.user_id) {
      toast.error('Please select a user');
      return;
    }

    if (formData.assignment_type === 'group' && !formData.group_id) {
      toast.error('Please select a group');
      return;
    }

    try {
      setAssigning(true);
      const response = await api.assessments.assign({
        assessment_id: Number(id),
        assignment_type: formData.assignment_type,
        target_role: 'learner',
        user_id: formData.assignment_type === 'user' ? Number(formData.user_id) : null,
        group_id: formData.assignment_type === 'group' ? Number(formData.group_id) : null,
        available_from: formData.available_from,
        available_to: formData.available_to
      });

      toast.success(response.data?.message || 'Assignment completed successfully.');
      setFormData((prev) => ({
        ...prev,
        user_id: '',
        group_id: ''
      }));
      await fetchInitial();
      if (formData.assignment_type === 'user') {
        await loadUsersFromMaster();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign assessment');
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteClick = (assignment) => {
    if (!canRemoveAssignment(assignment)) {
      toast.error('This user cannot be removed because the assessment has already started.');
      return;
    }
    setDeleteTarget(assignment);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setRemovingId(deleteTarget.id);
      const response = await api.assessments.removeAssignment(deleteTarget.id);
      toast.success(response.data?.message || 'User removed successfully.');
      setDeleteTarget(null);
      await fetchInitial();
      if (formData.assignment_type === 'user') {
        await loadUsersFromMaster();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove assignment');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-h-96 p-4">
        <Spinner animation="border" role="status" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex items-center gap-4 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => navigate('/admin/assessments')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
        >
          <FaArrowLeft className="w-4 h-4" /> Back
        </button>
        <div>
          <h5 className="mb-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Assessment Assignment
          </h5>
          {assessmentTitle ? (
            <p className="mb-0 mt-1 text-sm text-gray-600">
              Assessment: <strong className="text-purple-700">{assessmentTitle}</strong>
            </p>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <Row className="g-3">
          <Col md={4}>
            <Form.Label>Assignment Type</Form.Label>
            <Form.Select
              value={formData.assignment_type}
              onChange={(e) => handleAssignmentTypeChange(e.target.value)}
              aria-label="Assignment type"
            >
              <option value="user">User</option>
              <option value="group">Group</option>
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Role</Form.Label>
            <Form.Select value="learner" disabled aria-label="Role">
              <option value="learner">Learner</option>
            </Form.Select>
          </Col>
          <Col md={4}>
            {formData.assignment_type === 'user' ? (
              <>
                <Form.Label>User</Form.Label>
                <Form.Select
                  value={formData.user_id}
                  disabled={usersLoading}
                  onChange={(e) => setFormData((prev) => ({ ...prev, user_id: e.target.value }))}
                  aria-label="Select user"
                >
                  <option value="">
                    {usersLoading ? 'Loading users...' : 'Select Learner'}
                  </option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </option>
                  ))}
                </Form.Select>
                {!usersLoading && availableUsers.length === 0 ? (
                  <Form.Text className="text-muted">
                    No available non-organization learners found (already enrolled users are hidden).
                  </Form.Text>
                ) : null}
              </>
            ) : (
              <>
                <Form.Label>Group</Form.Label>
                <Form.Select
                  value={formData.group_id}
                  onChange={(e) => setFormData((prev) => ({ ...prev, group_id: e.target.value }))}
                  aria-label="Select group"
                >
                  <option value="">Select Group</option>
                  {platformGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
                {platformGroups.length === 0 ? (
                  <Form.Text className="text-muted">No non-organization groups available.</Form.Text>
                ) : null}
              </>
            )}
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col md={6}>
            <Form.Label>Schedule Start</Form.Label>
            <Form.Control
              type="datetime-local"
              value={formData.available_from}
              onChange={(e) => setFormData((prev) => ({ ...prev, available_from: e.target.value }))}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Schedule End</Form.Label>
            <Form.Control
              type="datetime-local"
              value={formData.available_to}
              onChange={(e) => setFormData((prev) => ({ ...prev, available_to: e.target.value }))}
            />
          </Col>
        </Row>

        <Button
          bsPrefix="custom-hamburger"
          onClick={handleAssign}
          disabled={assigning}
          className="mt-3 border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
        >
          {assigning ? 'Assigning...' : 'Assign'}
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h6 className="mb-0">Enrolled Users</h6>
          <Badge bg="primary">{assignments.length} enrolled</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="table table-bordered mb-0">
            <thead className="bg-purple-50">
              <tr>
                <th>Username</th>
                <th>Role</th>
                <th>Schedule Start</th>
                <th>Schedule End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">No enrolled users yet.</td>
                </tr>
              ) : (
                assignments.map((item) => {
                  const removable = canRemoveAssignment(item);
                  return (
                    <tr key={item.id}>
                      <td>{getUserDisplayName(item.user)}</td>
                      <td>
                        <Badge bg="secondary">{getAssignmentRoleName(item)}</Badge>
                      </td>
                      <td>{formatScheduleDate(item.available_from)}</td>
                      <td>{formatScheduleDate(item.available_to)}</td>
                      <td>
                        {removable ? (
                          <Button
                            bsPrefix="custom-hamburger"
                            size="sm"
                            onClick={() => handleDeleteClick(item)}
                            disabled={removingId === item.id}
                            title="Remove assignment"
                            className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                          >
                            {removingId === item.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <FaTrash />
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={Boolean(deleteTarget)} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Are you sure you want to remove this user from the assessment?</p>
          <p className="mb-0 text-muted">After removal, the user can be reassigned to this assessment.</p>
          {deleteTarget ? (
            <p className="mt-3 mb-0">
              <strong>{getUserDisplayName(deleteTarget.user)}</strong>
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={Boolean(removingId)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={Boolean(removingId)}>
            {removingId ? 'Removing...' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssessmentAssignments;
